import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Select } from '@mui/material';

// ============================================
// Design System Colors
// ============================================
export const COLORS = {
  primary: '#4AB3BC',
  primaryHover: '#4AB3BC',
  primaryLight: '#4AB3BC',
  primaryBg: 'rgba(15, 127, 133, 0.1)',

  // Event Card Colors
  privateLessonBg: '#0F7F85',
  privateLessonPast: '#8CD4D7',
  consultationBg: '#64B5F6',
  consultationPast: '#90CAF9',
  consultationBorder: '#42A5F5',

  // Group Lesson Status Colors
  groupPast: '#D9D9D9',
  groupPastBadge: '#BFBFBF',
  groupPastFont: '#404040',
  groupClosed: '#EDEDED',
  groupClosedBadge: '#D1D1D1',
  groupFull: '#DDF4E6',
  groupFullBadge: '#A7E3BE',
  groupFullFont: '#247E46',
  groupEmpty: '#FCE3DE',
  groupEmptyBadge: '#F6B6A9',
  groupEmptyFont: '#9E230B',
  groupPartial: '#FFF0DE',
  groupPartialBadge: '#FFD9A9',
  groupPartialFont: '#B96800',

  // Common
  white: '#FFFFFF',
  black: '#000000',
  gray: '#666',
  grayLight: '#f5f5f5',
  grayBorder: '#ddd',
  grayText: '#333',
  red: '#f44336',
  redSunday: '#e74c3c',
  blueSaturday: '#3498db',
};

// 월별 패널 강사(임직원) 색 점 팔레트 — 통합 임직원 목록 순서 기준 고정
export const TRAINER_PALETTE = ['#0F7F85', '#4AB3BC', '#64B5F6', '#5C6BC0', '#B96800', '#247E46'];

// 월/주 보기 이벤트 유형별 색·배지 (개인/듀엣/상담/그룹)
export const EVENT_TYPE_META = {
  pt: { label: '개인', color: '#0F7F85', badgeBg: 'rgba(15, 127, 133, 0.08)' },
  duet: { label: '듀엣', color: '#58B9C1', badgeBg: 'rgba(88, 185, 193, 0.14)' },
  ot: { label: 'OT', color: '#B45309', badgeBg: 'rgba(180, 83, 9, 0.12)' },
  consult: { label: '상담', color: '#64B5F6', badgeBg: 'rgba(100, 181, 246, 0.14)' },
  etc: { label: '기타', color: '#8E7CC3', badgeBg: 'rgba(142, 124, 195, 0.14)' },
  // cardBg: 주간 카드 배경 전용 연한 톤 (color는 배지·월간 바 텍스트용이라 가독성 위해 진하게 유지)
  group: { label: '그룹', color: '#B96800', badgeBg: 'rgba(185, 104, 0, 0.12)', cardBg: '#F1F3F4' },
};

// 출석 상태 메타 (스케줄 리뉴얼 시안 추출값) — 일간 카드, 주간 카드 배경, 상태 바, 카운트 텍스트, 툴팁 뱃지
export const ATTEND_STATUS_META = {
  // 일간 카드는 진한 배경 + 흰 글씨 (기존 개인레슨 카드 톤 유지), 주간 카드는 옅은 배경 + 어두운 글씨
  reserved: {
    label: '예약',
    countColor: '#5B6BC0',
    barColor: '#5B6BC0',
    dayBg: '#0F7F85',
    dayFont: '#FFFFFF',
    dayBorder: '#0C6B70',
    weekBg: '#FFFFFF',
    weekFont: '#404040',
    badgeBg: 'rgba(255, 255, 255, 0.24)',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: 'rgba(91, 107, 192, 0.12)',
    tooltipBadgeFont: '#5B6BC0',
  },
  attended: {
    label: '출석',
    countColor: '#1E7E78',
    barColor: '#1E7E78',
    dayBg: '#1E7E5A',
    dayFont: '#FFFFFF',
    dayBorder: '#1A6D4E',
    weekBg: '#D1EDEB',
    weekFont: '#14524E',
    badgeBg: 'rgba(255, 255, 255, 0.24)',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: '#E0F1EF',
    tooltipBadgeFont: '#0F5C57',
  },
  absented: {
    label: '결석',
    countColor: '#C7362A',
    barColor: '#C7362A',
    dayBg: '#C7362A',
    dayFont: '#FFFFFF',
    dayBorder: '#AE2E24',
    weekBg: '#FBE4E0',
    weekFont: '#404040',
    badgeBg: 'rgba(255, 255, 255, 0.24)',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: '#FBE7E5',
    tooltipBadgeFont: '#C7362A',
  },
};

// 주/월 보기에서 개인·듀엣이 아닌 이벤트의 상태 바 색 (그룹=중성 회색, 상담=유형색)
export const GROUP_BAR_COLOR = '#9AA3AA';

// 출석 상태가 아닌 요약 카운트(전체 일정 건수·그룹 건수·상담 건수)의 글자색
// OT 카드 색 — 개인·듀엣과 달리 배경을 유형색으로 칠하고 출석 상태는 카드 안 배지로만 보여 준다(일간).
// 주간은 옅은 유형색 배경 + 왼쪽 막대는 출석 상태색 유지.
export const OT_CARD_META = { dayBg: '#B45309', dayBorder: '#92400E', weekBg: '#FEF3C7' };

export const SUMMARY_EXTRA_COUNT_META = {
  schedule: { label: '스케줄', color: '#5A6468' },
  group: { label: '그룹', color: '#B96800' },
  ot: { label: 'OT', color: '#B45309' },
  consult: { label: '상담', color: '#64B5F6' },
  etc: { label: '기타', color: '#8E7CC3' },
};

// 그룹레슨 카드 색 (개인 스케줄 내) — 일간/주간 공통 밝은 회색 톤
export const GROUP_CARD_META = {
  dayBg: '#F5F6F7',
  dayFont: '#5A6468',
  dayBorder: '#E4E7E9',
  weekFont: '#5A6468',
  badgeBg: 'rgba(90, 100, 104, 0.12)',
};

// 유형 필터 = 그룹 단독일 때만 쓰는 예약 상태별 카드 색.
// 색값은 헤더 카운트 칩(예약마감/예약미달/예약없음)과 동일해서 카운트와 카드가 같은 색으로 읽힌다.
// 폐강은 카운트 칩에 없는 상태라 중립 회색(GROUP_CARD_META)을 그대로 쓴다.
export const GROUP_STATUS_CARD_META = {
  full: {
    label: '예약마감',
    barColor: '#247E46',
    dayBg: '#247E46',
    dayFont: '#FFFFFF',
    dayBorder: '#1C6538',
    weekBg: '#DFF3E7',
    weekFont: '#1B5E37',
    badgeBg: 'rgba(255, 255, 255, 0.24)',
    badgeFont: '#FFFFFF',
  },
  under: {
    label: '예약미달',
    barColor: '#B96800',
    dayBg: '#B96800',
    dayFont: '#FFFFFF',
    dayBorder: '#9C5700',
    weekBg: '#FCEEDC',
    weekFont: '#8A4E00',
    badgeBg: 'rgba(255, 255, 255, 0.24)',
    badgeFont: '#FFFFFF',
  },
  none: {
    label: '예약없음',
    barColor: '#9E230B',
    dayBg: '#9E230B',
    dayFont: '#FFFFFF',
    dayBorder: '#831C08',
    weekBg: '#FBE4E0',
    weekFont: '#7A1B08',
    badgeBg: 'rgba(255, 255, 255, 0.24)',
    badgeFont: '#FFFFFF',
  },
  closed: {
    label: '폐강',
    barColor: GROUP_BAR_COLOR,
    dayBg: GROUP_CARD_META.dayBg,
    dayFont: GROUP_CARD_META.dayFont,
    dayBorder: GROUP_CARD_META.dayBorder,
    weekBg: EVENT_TYPE_META.group.cardBg,
    weekFont: GROUP_CARD_META.weekFont,
    badgeBg: GROUP_CARD_META.badgeBg,
    badgeFont: GROUP_CARD_META.dayFont,
  },
};

// ============================================
// Container & Layout
// ============================================
// 헤더 3분할 — 좌/우 그룹이 남은 공간을 균등하게 나눠 가지므로 가운데 날짜가 화면 중앙에 오면서도
// 절대배치가 아니라서 좌/우 그룹과 절대 겹치지 않는다(겹침 대신 서로를 밀어낸다).
// 폭이 모자라면 아래 컨테이너 쿼리로 "날짜 → 요약칩·토글 → 버튼" 순서로 줄인다.
// 요약 칩(예약/출석/결석)은 어떤 폭에서도 숨기지 않는다.
// 사이드바 유무로 실제 헤더 폭이 달라지므로 @media 대신 헤더 자신을 기준으로 하는 컨테이너 쿼리를 쓴다.
// 2행 구성 — 1행은 날짜 네비게이션, 2행은 요약 카운트와 필터.
// 폭 경쟁이 사라져 축소 규칙은 1행(날짜)에만 남기고 2행은 자연 크기를 유지한다.
export const PageContainer = styled(Box)({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginBottom: '10px',
  position: 'relative',
  containerType: 'inline-size',
  containerName: 'plHeader',

  // 내비 옆 보조 버튼(오늘·설정) — 날짜보다 위계가 낮아 아웃라인 + 소형으로 통일. 글자는 필터 버튼과 같은 청록·굵기
  '.hdr-compact': {
    height: '30px',
    fontSize: '13px',
    fontWeight: 500,
    padding: '0 12px',
    minWidth: 0,
    marginLeft: '8px',
    background: '#fff',
    color: '#4AB3BC',
    border: '1px solid #9FD3D7',
    borderRadius: '6px',
    '&:hover': { background: 'rgba(74, 179, 188, 0.08)' },
  },

  '@container plHeader (max-width: 1300px)': {
    '.hdr-chips': { padding: '6px 10px', gap: '7px' },
    '.hdr-chip': { fontSize: '12px' },
    '.hdr-viewtoggle button, .hdr-typetoggle button': { fontSize: '14px', padding: '0 12px' },
    '.hdr-today, .hdr-filter, .hdr-settings': { fontSize: '14px' },
  },
  '@container plHeader (max-width: 1150px)': {
    '.hdr-date': { fontSize: '16px', minWidth: 0 },
    '.hdr-chips': { padding: '5px 8px', gap: '5px' },
    '.hdr-chip': { fontSize: '11px' },
    '.hdr-chip b': { marginLeft: '2px' },
    '.hdr-viewtoggle button, .hdr-typetoggle button': { fontSize: '13px', padding: '0 9px' },
    '.hdr-today, .hdr-filter, .hdr-settings': { fontSize: '13px', padding: '0 10px', minWidth: 0 },
    '.hdr-zoom button': { minWidth: '26px', width: '26px', height: '26px', fontSize: '17px' },
    '.hdr-zoom p': { fontSize: '14px', minWidth: '30px' },
    '.hdr-picker': { width: '30px', height: '30px' },
  },
  '@container plHeader (max-width: 1010px)': {
    '.hdr-date': { fontSize: '15px', margin: '0 4px' },
    '.hdr-nav svg': { width: '18px', height: '18px' },
    '.hdr-viewtoggle button, .hdr-typetoggle button': { padding: '0 7px' },
    '.hdr-today, .hdr-filter, .hdr-settings': { padding: '0 8px' },
  },
  // 좁은 폭 — 우측 absolute 뷰 전환이 ‹ 날짜 › · 오늘 · 시간 설정과 겹치므로 흐름 안 다음 줄 우측으로 내린다
  '@container plHeader (max-width: 900px)': {
    '.hdr-row': { flexWrap: 'wrap' },
    '.hdr-viewtoggle-wrap': {
      position: 'static',
      transform: 'none',
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-end',
    },
    // 왼쪽 absolute 설정 버튼도 날짜와 겹치므로 흐름 안 맨 앞으로 넣는다
    '.hdr-settings-wrap': {
      position: 'static',
      transform: 'none',
      order: -1,
    },
  },
});

// 1행 — 설정 | ← 날짜 → 오늘 | 뷰 전환 (내비게이션만 중앙 정렬)
export const HeaderRow = styled(Box)({
  width: '100%',
  position: 'relative',
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  minHeight: '36px',
});

// 2행 — 스케줄표 바로 위. 줌·날짜피커(좌) | 요약 카운트(정중앙) | 필터·뷰 전환(우)
// 카운트는 좌/우 그룹 폭 차이와 무관하게 화면 중앙에 고정한다 (absolute 센터링)
export const HeaderCountsRow = styled(Box)({
  width: '100%',
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  position: 'relative',
  minHeight: '40px',
  '& > .hdr-chips': {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
  },
});

// 유형 드롭다운 트리거 — '스케줄: 전체 ▾'
// 누를 때 살짝 눌리는 피드백을 줘서 인터페이스가 반응한다는 감각을 남긴다.
export const TypeFilterButton = styled('button', {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  height: '36px',
  padding: '0 10px 0 12px',
  border: `1px solid ${isActive ? '#4AB3BC' : '#D9DEE0'}`,
  borderRadius: '6px',
  background: isActive ? 'rgba(74, 179, 188, 0.08)' : '#fff',
  fontFamily: 'inherit',
  fontSize: '14px',
  fontWeight: 500,
  color: '#5A6468',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'transform 160ms cubic-bezier(0.23, 1, 0.32, 1), border-color 160ms ease, background-color 160ms ease',
  '&:hover': { background: isActive ? 'rgba(74, 179, 188, 0.12)' : '#F2F5F6' },
  '&:active': { transform: 'scale(0.97)' },
  '& b': {
    marginLeft: '4px',
    color: isActive ? '#33959D' : '#404040',
    fontWeight: 700,
  },
  '& svg': {
    fontSize: '20px',
    color: '#A6A6A6',
  },
}));

export const CalendarContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'isDragging',
})(({ isDragging }) => ({
  width: '100%',
  height: 'calc(100dvh - 180px)',
  overflowX: 'auto',
  overflowY: 'auto',
  cursor: isDragging ? 'grabbing' : 'grab',
  userSelect: isDragging ? 'none' : 'auto',
  // 그룹 스케줄 표(GroupLessonStyled.CalendarContainer)와 같은 바깥 테두리·모서리
  border: '1px solid #E5E7EB',
  borderRadius: '10px',
  background: '#fff',
}));

// ============================================
// Header Controls
// ============================================
// 좌/우 그룹: flex-basis 0 + grow 1 → 남는 공간을 균등 분배해 가운데 그룹이 시각적 중앙에 놓인다.
// minWidth: fit-content → 폭이 부족해도 자기 콘텐츠 아래로는 줄지 않아 가운데 그룹을 침범하지 않는다.
export const HeaderLeftGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  flex: '1 1 0',
  minWidth: 'fit-content',
});

export const HeaderCenterGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  flex: '0 1 auto',
  minWidth: 0,
});

export const HeaderRightGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  flex: '1 1 0',
  minWidth: 'fit-content',
  justifyContent: 'flex-end',
});

export const DateNavContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
});

// ‹ 날짜 › 오른쪽에 매달리는 보조 버튼 묶음 — 흐름 밖(absolute)이라 날짜 센터링에 영향을 주지 않는다
export const NavSideActions = styled(Box)({
  position: 'absolute',
  left: '100%',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
});

// 그룹 스케줄 주 제목(GroupLessonStyled.WeekTitle)과 같은 톤
export const DateTitle = styled(Typography)({
  margin: '0 6px',
  fontSize: '18px',
  fontWeight: 700,
  minWidth: '160px',
  textAlign: 'center',
  color: '#111827',
  letterSpacing: '-0.3px',
  whiteSpace: 'nowrap',
});

// 캘린더 헤더 컨트롤(TodayButton, NavButton, ZoomButton 등)은
// ../LessonCalendarStyled.js로 이동되어 group lesson과 공유됨

// 뷰 전환 3단 세그먼트 버튼 (월/주/일(강사별)) — 컨테이너는 LessonCalendarStyled.ViewToggleContainer 재사용
export const ViewSegmentButton = styled('button', {
  shouldForwardProp: prop => prop !== 'isActive' && prop !== 'hasBorder' && prop !== 'isCompact',
})(({ isActive, hasBorder, isCompact }) => ({
  height: '100%',
  border: 'none',
  padding: isCompact ? '0 14px' : '0 18px',
  fontSize: '15px',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  background: isActive ? COLORS.primary : COLORS.white,
  color: isActive ? COLORS.white : COLORS.primary,
  borderLeft: hasBorder ? `1px solid ${COLORS.primary}` : 'none',
  '&:hover': {
    background: isActive ? COLORS.primary : COLORS.primaryBg,
  },
}));

export const WeeklyTrainerSelect = styled(Select)({
  minWidth: '120px',
  height: '36px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: COLORS.primary,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: COLORS.primaryHover,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: COLORS.primary,
  },
});

// ============================================
// Calendar Table
// ============================================
// 빈 컬럼 헤더의 '직원 등록' 안내 버튼 — 강사명과 같은 자리, 낮은 위계(점선 테두리)
export const FillerRegisterButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '6px 0',
  padding: '4px 12px',
  background: 'transparent',
  color: '#9AA6AB',
  border: '1px dashed #C4CDD1',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'color 160ms ease, border-color 160ms ease',
  '&:hover': { color: '#33959D', borderColor: '#9FD3D7' },
});

export const CalendarTable = styled('table', {
  shouldForwardProp: prop => prop !== 'columnCount',
})(({ columnCount }) => ({
  width: `${columnCount * 250 + 60}px`,
  // 그룹 스케줄 표처럼 separate — sticky 머리글·시간 열의 테두리가 스크롤해도 칸에 붙어 다닌다
  borderCollapse: 'separate',
  borderSpacing: 0,
  tableLayout: 'fixed',
}));

// 컬럼 드래그 시 "여기로 들어간다"를 알려주는 굵은 세로 막대. dropEdge 쪽 가장자리에만 그린다.
const DROP_EDGE_BAR_PX = 6;

const dropEdgeShadow = dropEdge =>
  dropEdge === 'left'
    ? `inset ${DROP_EDGE_BAR_PX}px 0 0 ${COLORS.primary}`
    : `inset -${DROP_EDGE_BAR_PX}px 0 0 ${COLORS.primary}`;

export const TableHeaderCell = styled('th', {
  shouldForwardProp: prop =>
    !['isTimeColumn', 'isToday', 'isDragSource', 'isDropTarget', 'dropEdge'].includes(prop),
})(({ isTimeColumn, isToday, isDragSource, isDropTarget, dropEdge }) => ({
  textAlign: 'center',
  // 그룹 스케줄 표 머리글과 같은 톤
  borderBottom: '2px solid #e0e0e0',
  borderRight: '1px solid #f0f0f0',
  backgroundColor: isToday ? '#e8f5f5' : '#fafafa',
  padding: isTimeColumn ? '10px' : '3px',
  minWidth: isTimeColumn ? '60px' : '250px',
  maxWidth: isTimeColumn ? '60px' : '250px',
  width: isTimeColumn ? '60px' : '250px',
  position: 'sticky',
  top: 0,
  left: isTimeColumn ? 0 : 'auto',
  zIndex: isTimeColumn ? 1001 : 1000,
  overflow: 'hidden',
  '&:last-child': { borderRight: 'none' },
  ...(isTimeColumn && {
    color: COLORS.white,
  }),
  ...(isDragSource && {
    opacity: 0.25,
  }),
  ...(isDropTarget && {
    backgroundColor: '#e6f7f7',
    boxShadow: dropEdge
      ? `inset 0 0 0 2px ${COLORS.primary}, ${dropEdgeShadow(dropEdge)}`
      : `inset 0 0 0 2px ${COLORS.primary}`,
  }),
}));

export const TrainerHeaderLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#4B5563',
  letterSpacing: '-0.2px',
  cursor: 'grab',
  userSelect: 'none',
  '&:active': {
    cursor: 'grabbing',
  },
});

export const TrainerHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
});

export const TrainerArrowButton = styled('button')({
  width: '30px',
  height: '30px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#737373',
  padding: 0,
  transition: 'all 0.15s ease',
  '&:hover:not(:disabled)': {
    backgroundColor: '#4AB3BC',
    borderColor: '#4AB3BC',
    color: '#fff',
    borderRadius: '20px',
  },
  '&:disabled': {
    opacity: 0.3,
    cursor: 'not-allowed',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
  },
});

// 시간 열 글자·여백. 기본 모양(여백 5px + 16px 글자, 줄높이 1.5 기준 34px)이 행보다 크면 표가 행을 늘려
// 카드 위치·드래그 스냅·현재시각선이 어긋난다. 그보다 낮은 행(30분 단위 + 축소)은 여백을 빼고 글자를 행에 맞춰 줄인다.
const TIME_CELL_FONT_PX = 11;
const TIME_CELL_PADDING_PX = 4;
const TIME_CELL_COMPACT_BELOW_PX = TIME_CELL_PADDING_PX * 2 + TIME_CELL_FONT_PX * 1.5;
const TIME_CELL_COMPACT_FONT_RATIO = 0.6;

export const TimeCell = styled('td', {
  shouldForwardProp: prop => prop !== 'cellHeight',
})(({ cellHeight = 80 }) => ({
  textAlign: 'center',
  // 그룹 스케줄 표 시간 열과 같은 톤
  backgroundColor: '#FAFBFC',
  borderBottom: '1px solid #F3F4F6',
  borderRight: '1px solid #E5E7EB',
  color: '#9CA3AF',
  fontWeight: 500,
  fontSize: `${TIME_CELL_FONT_PX}px`,
  padding: `${TIME_CELL_PADDING_PX}px`,
  ...(cellHeight < TIME_CELL_COMPACT_BELOW_PX && {
    fontSize: `${Math.min(TIME_CELL_FONT_PX, Math.floor(cellHeight * TIME_CELL_COMPACT_FONT_RATIO))}px`,
    lineHeight: 1,
    padding: `0 ${TIME_CELL_PADDING_PX}px`,
  }),
  position: 'sticky',
  top: 0,
  left: 0,
  zIndex: 998,
  height: `${cellHeight}px`,
  overflow: 'hidden',
}));

export const EventCell = styled('td', {
  shouldForwardProp: prop =>
    !['isToday', 'columnCount', 'cellHeight', 'isDraggingEvent', 'isDragSource', 'isDropTarget', 'dropEdge'].includes(
      prop,
    ),
})(({ isToday, columnCount, cellHeight = 80, isDraggingEvent, isDragSource, isDropTarget, dropEdge }) => ({
  position: 'relative',
  height: `${cellHeight}px`,
  minWidth: columnCount && columnCount <= 3 ? '150px' : '250px',
  width: columnCount && columnCount <= 3 ? `${100 / columnCount}%` : '250px',
  borderBottom: '1px solid #f0f0f0',
  borderRight: '1px solid #f0f0f0',
  '&:last-child': { borderRight: 'none' },
  verticalAlign: 'top',
  cursor: 'pointer',
  overflow: 'visible',
  backgroundColor: isToday ? 'rgba(15, 127, 133, 0.03)' : 'transparent',
  // 컬럼 드래그 중 — 들어올린 열은 빠져나간 자리처럼 흐리게, 놓을 열은 강조.
  ...(isDragSource && {
    opacity: 0.25,
  }),
  ...(isDropTarget && {
    backgroundColor: '#e6f7f7',
    boxShadow: dropEdge
      ? `inset 2px 0 0 ${COLORS.primary}, inset -2px 0 0 ${COLORS.primary}, ${dropEdgeShadow(dropEdge)}`
      : `inset 2px 0 0 ${COLORS.primary}, inset -2px 0 0 ${COLORS.primary}`,
  }),
  // 호버 시 "여기 클릭하면 생성" 미리보기. 단, 이벤트 카드 hover 시 / 카드 드래그 중에는 표시 안 함.
  // 박스 위치·높이는 선택 단위 구간 — 마우스 이동 때 셀에 --hover-top/--hover-height(px)를 직접 넣는다.
  // 값이 없으면 셀 전체(기존 모양). 구간이 셀 밖으로 걸치면 위·아래 행까지 넘어 그린다.
  ...(!isDraggingEvent && {
    '&:hover:not(:has(.event-card:hover))::after': {
      content: '""',
      position: 'absolute',
      top: 'calc(var(--hover-top, 0px) + 2px)',
      left: '2px',
      right: '2px',
      height: 'calc(var(--hover-height, 100%) - 4px)',
      boxSizing: 'border-box',
      backgroundColor: 'rgba(74, 179, 188, 0.15)',
      border: '2px dashed #4AB3BC',
      borderRadius: '6px',
      pointerEvents: 'none',
      zIndex: 0,
    },
  }),
}));

export const RowDivider = styled('div')({
  position: 'absolute',
  left: 0,
  right: 0,
  top: '50%',
  height: '1px',
  backgroundColor: '#F3F4F6',
  pointerEvents: 'none',
  zIndex: 1,
});

// ============================================
// Current Time Indicator
// ============================================
export const CurrentTimeIndicator = styled(Box, {
  shouldForwardProp: prop => prop !== 'topPosition',
})(({ topPosition }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: `${topPosition}px`,
  height: '2px',
  backgroundColor: COLORS.red,
  zIndex: 10,
  pointerEvents: 'none',
}));

export const CurrentTimeDot = styled('div')({
  position: 'absolute',
  left: '-5px',
  top: '-4px',
  width: '10px',
  height: '10px',
  backgroundColor: COLORS.red,
  borderRadius: '50%',
  zIndex: 10,
});

// ============================================
// Drag Ghost & Time Label
// ============================================
export const DragGhostCard = styled('div')({
  backgroundColor: 'rgba(74, 179, 188, 0.15)',
  border: '2px dashed #4AB3BC',
  borderRadius: '6px',
  pointerEvents: 'none',
});

export const DragTimeLabel = styled('div')({
  backgroundColor: '#0F7F85',
  color: '#fff',
  fontSize: '11px',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: '4px',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
});

// ============================================
// Event Card Base
// ============================================
// 5~10분처럼 짧은 수업도 정보가 보이도록 카드에 최소 높이를 둔다(줌에 따라 같이 커진다).
// 지점 스케줄은 설정의 '카드 최소 높이(분)'를 픽셀로 바꿔 cardMinHeight 로 넘긴다(기본 48분 = 1시간의 80%).
// 넘기지 않으면 기존대로 1시간 칸의 80% 다.
// 시작 위치(top)는 실제 시간 그대로 두므로, 이 높이 때문에 뒤 카드와 겹칠 수 있다.
// 겹치면 늦게 시작한 카드가 위에 오고(시간 셀 DOM 순서), hover 한 카드만 최상단으로 떠오른다.
const DEFAULT_EVENT_CARD_MIN_HEIGHT = '80%';

// 겹쳐 가려지는 카드가 최소한 이만큼은 보여야 첫 줄(시간)을 읽을 수 있다.
// 시간 줄 글자 약 13px + 위 padding 2px + 테두리 1px + 여유.
export const MIN_CARD_EXPOSED_HEIGHT = 18;

export const EventCardBase = styled(Box, {
  shouldForwardProp: prop =>
    !['bgColor', 'cardHeight', 'cardTop', 'cardWidth', 'cardLeft', 'cardZIndex', 'cardMinHeight'].includes(prop),
})(({ bgColor, cardHeight, cardTop, cardWidth = '100%', cardLeft = 0, cardZIndex = 2, cardMinHeight }) => ({
  position: 'absolute',
  backgroundColor: bgColor,
  width: cardWidth,
  height: cardHeight,
  minHeight: cardMinHeight || DEFAULT_EVENT_CARD_MIN_HEIGHT, // % 는 시간 칸(EventCell) 높이 기준
  top: cardTop,
  left: cardLeft,
  padding: '0px 8.5px',
  overflow: 'hidden',
  cursor: 'pointer',
  zIndex: cardZIndex,
  fontSize: 'clamp(0.65rem, 1vw, 0.75rem)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'stretch',
  boxSizing: 'border-box',
  border: '1px solid #E0E0E0',
  // 겹쳐 가려진 카드를 마우스로 끌어올린다 — 크기는 그대로 두고 순서와 그림자만 바꾼다.
  // 시간축(TimeCell)의 z-index 998 아래를 유지해야 좌측 시간이 카드에 덮이지 않는다.
  '&:hover': {
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.22)',
  },
}));

// 다른 화면(회원상세 예약 내역 등)에서 특정 수업으로 넘어왔을 때의 포커스 링 — ConsultationCard와 동일 규칙.
const classFocusFlashStyle = {
  transition: 'box-shadow 200ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&.focus-flash': {
    boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.9), 0 0 0 6px rgba(15, 127, 133, 0.55)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
};

export const PrivateLessonCard = styled(EventCardBase)({
  flexDirection: 'row',
  gap: 'clamp(2px, 0.3vw, 4px)',
  ...classFocusFlashStyle,
});

export const GroupLessonCard = styled(EventCardBase)({
  color: COLORS.black,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  containerType: 'size',
  padding: '0px 8px',
  // 여러 명 OT 가 이 카드를 쓰고, 충돌 포커스 대상이 될 수 있다
  ...classFocusFlashStyle,
});

export const ConsultationCard = styled(EventCardBase, {
  shouldForwardProp: prop => !['bgColor', 'cardHeight', 'cardTop', 'cardMinHeight'].includes(prop),
})(({ bgColor }) => ({
  backgroundColor: bgColor,
  // 링은 transition 으로만 켜고 끈다 — 다른 상담으로 연달아 이동해도 중간에 잘리지 않는다.
  transition: 'box-shadow 200ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&.focus-flash': {
    boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.9), 0 0 0 6px rgba(15, 127, 133, 0.55)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

export const DuetLessonCard = styled(EventCardBase, {
  shouldForwardProp: prop =>
    !['bgColor', 'cardHeight', 'cardTop', 'cardWidth', 'cardLeft', 'cardZIndex', 'isOverlay', 'cardMinHeight'].includes(
      prop,
    ),
})(({ isOverlay }) => ({
  boxShadow: isOverlay ? '-2px 0 4px rgba(0,0,0,0.1)' : 'none',
  borderRadius: isOverlay ? '8px 0 0 8px' : '0',
  ...classFocusFlashStyle,
}));

// ============================================
// Event Card Content
// ============================================
export const EventCardContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 'clamp(2px, 0.3vw, 4px)',
  overflow: 'hidden',
  flex: 1,
  justifyContent: 'center',
  minWidth: 0,
  width: 0,
});

export const EventCardText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'fontColor' && prop !== 'isBold',
})(({ fontColor = COLORS.white, isBold }) => ({
  margin: '0px',
  lineHeight: '1.2',
  fontSize: '12px',
  color: fontColor,
  fontWeight: isBold ? 'bold' : 'normal',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '100%',
  maxWidth: '100%',
}));

export const EventCardMemberInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 'clamp(2px, 0.3vw, 4px)',
  overflow: 'hidden',
  minWidth: 0,
  flexShrink: 0,
});

// 상담 카드 공통 프레임 — 배경·테두리는 결과와 무관하게 하나로 둔다.
// 색으로 배경을 나누면 겹친 카드에서 얼룩덜룩해져 "상담"이라는 종류 자체가 흐려진다.
// 결과 구분은 결과색 뱃지가 전담한다.
export const CONSULT_CARD_META = {
  dayBg: '#64B5F6',
  dayBorder: '#42A5F5',
  dayFont: '#FFFFFF',
};

// 기타 일정 카드 메타 — 상담과 같은 프레임을 쓰되 색만 분리한다(보라 계열).
// 결과·대상 같은 상태 개념이 없어 뱃지는 '기타' 라벨 하나뿐이다.
export const ETC_CARD_META = {
  dayBg: '#8E7CC3',
  dayBorder: '#7B68B0',
  dayFont: '#FFFFFF',
  badgeBg: 'rgba(255, 255, 255, 0.24)',
  badgeFont: '#FFFFFF',
  shortLabel: '기타',
  tooltipBadgeBg: 'rgba(142, 124, 195, 0.14)',
  tooltipBadgeFont: '#6B5BA0',
};

// 상담 결과 메타 — 카드 뱃지(흰 배경 + 결과색)와 툴팁 뱃지 색만 갖는다.
export const CONSULT_RESULT_META = {
  SCHEDULED: {
    label: '예정',
    shortLabel: '예정',
    badgeBg: '#1E4FA3',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: '#E7EDFC',
    tooltipBadgeFont: '#2F55A4',
  },
  // 상담 일정 자체를 바꾼 상태 — 예정(남색)과 이어지는 하늘색
  RESCHEDULED: {
    label: '예약 스케줄 변경',
    shortLabel: '스케줄변경',
    badgeBg: '#0284C7',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: '#E0F2FE',
    tooltipBadgeFont: '#0369A1',
  },
  // 종료시간이 지나도록 예정으로 남아 새벽 배치가 전환한 상태 — 회색 계열
  UNCONFIRMED: {
    label: '미확인',
    shortLabel: '미확인',
    badgeBg: '#6B7280',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: '#ECEEF2',
    tooltipBadgeFont: '#4B5563',
  },
  NO_SHOW: {
    label: '미방문',
    shortLabel: '미방문',
    badgeBg: '#C7362A',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: '#ECEEF2',
    tooltipBadgeFont: '#5A6377',
  },
  // 방문은 앰버 — 초록 계열(신규등록·재등록 아님)과 색상 자체가 갈리게 둔다.
  VISITED: {
    label: '방문',
    shortLabel: '방문',
    badgeBg: '#B45309',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: '#FDF0E1',
    tooltipBadgeFont: '#9A4A08',
  },
  CONSIDERING: {
    label: '고민 중',
    shortLabel: '고민 중',
    badgeBg: '#A21CAF',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: '#F6E6F8',
    tooltipBadgeFont: '#8B1A96',
  },
  NOT_REGISTERED: {
    label: '미등록',
    shortLabel: '미등록',
    badgeBg: '#44403C',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: '#EEECEA',
    tooltipBadgeFont: '#44403C',
  },
  NEW_REGISTERED: {
    label: '신규등록',
    shortLabel: '신규등록',
    badgeBg: '#1F7343',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: '#DFF3E7',
    tooltipBadgeFont: '#1F7343',
  },
  RE_REGISTERED: {
    label: '재등록',
    shortLabel: '재등록',
    badgeBg: '#5B21B6',
    badgeFont: '#FFFFFF',
    tooltipBadgeBg: '#EDE6FA',
    tooltipBadgeFont: '#5B21B6',
  },
};

// 유형 필터 = 상담 단독일 때만 쓰는 결과별 카드 색.
// 배경색은 CONSULT_RESULT_META.badgeBg(= 카운트 칩 색)와 같은 값이라 카운트와 카드가 같은 색으로 읽힌다.
// 배경이 결과색을 전담하므로 결과 뱃지는 개인 카드와 같은 흰 반투명으로 바꿔 배경과 겹치지 않게 한다.
// 결과색(카운트 칩·뱃지와 동일값) 하나에서 카드 색 한 벌을 만든다.
// 주간 카드도 상담은 원래 진한 배경 + 흰 글씨라 일간과 같은 톤을 그대로 쓴다.
const buildConsultCardMeta = (color, borderColor) => ({
  barColor: color,
  dayBg: color,
  dayBorder: borderColor,
  dayFont: '#FFFFFF',
  weekBg: color,
  weekFont: '#FFFFFF',
  badgeBg: 'rgba(255, 255, 255, 0.24)',
  badgeFont: '#FFFFFF',
});

export const CONSULT_RESULT_CARD_META = {
  SCHEDULED: buildConsultCardMeta('#1E4FA3', '#17407F'),
  RESCHEDULED: buildConsultCardMeta('#0284C7', '#036A9F'),
  UNCONFIRMED: buildConsultCardMeta('#6B7280', '#565D69'),
  NO_SHOW: buildConsultCardMeta('#C7362A', '#AE2E24'),
  VISITED: buildConsultCardMeta('#B45309', '#96450A'),
  CONSIDERING: buildConsultCardMeta('#A21CAF', '#86188F'),
  NOT_REGISTERED: buildConsultCardMeta('#44403C', '#33302D'),
  NEW_REGISTERED: buildConsultCardMeta('#1F7343', '#185C36'),
  RE_REGISTERED: buildConsultCardMeta('#5B21B6', '#4A1A95'),
};

// 상담 대상 메타 — 카드 1행 오른쪽 끝 고정 칩. 회원/잠재/비회원을 카드만 보고 구분한다.
export const CONSULT_TARGET_META = {
  MEMBER: { label: '회원' },
  TEMPORARY: { label: '잠재' },
  NON_MEMBER: { label: '비회원' },
};

// 대상 칩 — 결과 뱃지(채워진 반투명)와 위계를 다르게 두려고 테두리만 쓴다.
export const ConsultTargetChip = styled('span')({
  flex: '0 0 auto',
  marginLeft: 'auto',
  paddingLeft: '6px',
  border: '1px solid rgba(255, 255, 255, 0.75)',
  borderRadius: '999px',
  padding: '0 6px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
});

// ============================================
// Group Lesson Badge
// ============================================
export const GroupLessonContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '1px',
  overflow: 'hidden',
  flex: 1,
  minWidth: 0,
});

export const GroupLessonTitle = styled(Typography)({
  margin: 0,
  fontSize: '12px',
  fontWeight: 600,
  color: COLORS.black,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.3,
});

export const GroupLessonInfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '11px',
  fontWeight: 500,
  color: '#595959',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  '@container (width < 120px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '1px',
  },
});

export const GroupLessonTrainerText = styled('span')({
  fontSize: '11px',
  color: '#595959',
  whiteSpace: 'nowrap',
  '@container (height < 30px)': {
    display: 'none',
  },
});

export const GroupLessonBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'bgColor',
})(({ bgColor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: bgColor,
  padding: '0 6px',
  height: '20px',
  borderRadius: '16px',
  flexShrink: 0,
  marginLeft: '6px',
}));

export const GroupLessonBadgeText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'fontColor',
})(({ fontColor }) => ({
  color: fontColor,
  fontWeight: 600,
  fontSize: '11px',
  whiteSpace: 'nowrap',
}));

// 기간 이동 리페치 중 표시하는 플로팅 인디케이터 (이전 데이터 유지 중임을 알림, CommonLoading 톤 재사용)
export const FetchingBadge = styled(Box)({
  position: 'absolute',
  top: '64px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 14px',
  backgroundColor: COLORS.white,
  border: '1px solid #e0e0e0',
  borderRadius: '999px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
  fontSize: '13px',
  fontWeight: 500,
  color: '#94A3B8',
  zIndex: 1100,
  pointerEvents: 'none',
});

// ============================================
// 월/주 분할형 공통 레이아웃 + 선택일 상세 패널
// ============================================
export const SplitViewLayout = styled(Box)({
  display: 'flex',
  gap: '16px',
  alignItems: 'stretch',
});

export const DetailPanel = styled(Box, {
  shouldForwardProp: prop => prop !== 'panelWidth' && prop !== 'panelMaxHeight',
})(({ panelWidth = 440, panelMaxHeight }) => ({
  width: `${panelWidth}px`,
  flexShrink: 0,
  border: '1px solid #e0e0e0',
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  alignSelf: 'stretch',
  backgroundColor: COLORS.white,
  // 좌측 달력 그리드 높이에 패널을 맞춰 내부 스크롤을 강제 — 무한 스크롤이 항상 동작하게 함
  ...(panelMaxHeight ? { maxHeight: `${panelMaxHeight}px` } : {}),
}));

export const DetailPanelHeader = styled(Box)({
  padding: '16px 20px',
  borderBottom: '1px solid #f0f0f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const DetailPanelTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 700,
  color: '#262626',
});

export const DetailPanelCountPill = styled('span')({
  background: 'rgba(15, 127, 133, 0.1)',
  color: '#0F7F85',
  borderRadius: '999px',
  padding: '3px 12px',
  fontSize: '13px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
});

export const DetailPanelBody = styled(Box, {
  shouldForwardProp: prop => prop !== 'bodyPadding',
})(({ bodyPadding = 0 }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: bodyPadding,
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-thumb': { background: '#d0d0d0', borderRadius: '2px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  scrollbarWidth: 'thin',
  scrollbarColor: '#d0d0d0 transparent',
}));

export const DetailPanelFooter = styled(Box)({
  padding: '12px 20px',
  borderTop: '1px solid #f0f0f0',
  display: 'flex',
  justifyContent: 'flex-end',
});

export const DetailPanelFooterLink = styled('span')({
  fontSize: '13px',
  fontWeight: 600,
  color: '#0F7F85',
  cursor: 'pointer',
});

// 주간 패널 상단 "+ 일정 추가" 버튼 — 패널 전체 폭 채움형
export const DetailPanelAddButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  height: '42px',
  margin: '14px 16px 0',
  fontSize: '14px',
  fontWeight: 700,
  color: COLORS.white,
  background: COLORS.primary,
  border: 'none',
  borderRadius: '10px',
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#3FA0A8',
  },
});

// 패널 셸 — 리사이즈 핸들을 패널 왼쪽 엣지에 겹쳐 배치하기 위한 래퍼
export const PanelSideWrapper = styled(Box)({
  position: 'relative',
  flexShrink: 0,
  display: 'flex',
  alignSelf: 'stretch',
});

// 패널 좌측 엣지 가로폭 드래그 핸들 (SplitViewLayout gap 16px 안에 위치)
export const PanelResizeHandle = styled('div')({
  position: 'absolute',
  left: '-11px',
  top: 0,
  bottom: 0,
  width: '8px',
  cursor: 'col-resize',
  borderRadius: '4px',
  zIndex: 5,
  '&:hover': { backgroundColor: 'rgba(74, 179, 188, 0.35)' },
});

// 접힘 상태 우측 가장자리 rail — 세로 중앙 탭(«·상세·건수 배지) 클릭 시 펼침
export const PanelCollapsedRail = styled('button')({
  width: '34px',
  flexShrink: 0,
  alignSelf: 'stretch',
  position: 'relative',
  // 버튼 기본 세로 중앙 정렬을 해제 — sticky 앵커 기준을 rail 상단으로 고정
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  '&:hover .collapsed-tab': {
    borderColor: COLORS.primary,
    backgroundColor: 'rgba(74, 179, 188, 0.06)',
  },
});

// 접힘 탭 sticky 앵커 — 스크롤해도 탭이 화면 세로 중앙을 따라오게 함 (rail 범위 내 클램프)
export const PanelCollapsedTabAnchor = styled('span')({
  position: 'sticky',
  top: 'calc(50vh - 60px)',
  display: 'block',
  height: 0,
});

export const PanelCollapsedTab = styled('span')({
  position: 'absolute',
  top: 0,
  left: '2px',
  width: '32px',
  padding: '10px 0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  background: COLORS.white,
  border: '1px solid #e0e0e0',
  borderRight: 'none',
  borderRadius: '14px 0 0 14px',
  boxShadow: '-2px 2px 8px rgba(0, 0, 0, 0.08)',
  color: '#0F7F85',
  '& .MuiSvgIcon-root': { fontSize: '18px' },
});

export const PanelCollapsedTabText = styled('span')({
  writingMode: 'vertical-rl',
  fontSize: '13px',
  fontWeight: 700,
  color: '#0F7F85',
  letterSpacing: '2px',
});

export const PanelCollapsedBadge = styled('span')({
  minWidth: '20px',
  height: '20px',
  borderRadius: '999px',
  background: '#0F7F85',
  color: '#fff',
  fontSize: '11px',
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 4px',
  boxSizing: 'border-box',
});

// 펼침 상태 핸들 탭 sticky 앵커 — 화면 세로 중앙 추종 (패널 범위 내 클램프)
export const PanelEdgeTabAnchor = styled(Box)({
  position: 'sticky',
  top: 'calc(50vh - 15px)',
  alignSelf: 'flex-start',
  width: 0,
  height: 0,
  zIndex: 6,
});

// 펼침 상태 패널 좌측 핸들 탭 — 접기 버튼(»). 폭 조절은 좌측 엣지 전체 드래그
export const PanelEdgeTab = styled(Box)({
  position: 'absolute',
  left: '-13px',
  top: 0,
  width: '26px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  background: COLORS.white,
  border: '1px solid #e0e0e0',
  borderRadius: '13px',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  zIndex: 6,
  overflow: 'hidden',
});

export const PanelEdgeCollapseButton = styled('button')({
  width: '100%',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: '#0F7F85',
  padding: 0,
  '&:hover': { backgroundColor: 'rgba(74, 179, 188, 0.12)' },
  '& .MuiSvgIcon-root': { fontSize: '16px' },
});

export const EventTypeBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'badgeColor' && prop !== 'badgeBg',
})(({ badgeColor, badgeBg }) => ({
  fontSize: '11px',
  fontWeight: 600,
  color: badgeColor,
  background: badgeBg,
  borderRadius: '4px',
  padding: '2px 8px',
  flexShrink: 0,
  whiteSpace: 'nowrap',
}));

// ============================================
// 월별 보기 (Month View)
// ============================================
export const MonthGridContainer = styled(Box)({
  flex: 1,
  minWidth: 0,
  border: '1px solid #e0e0e0',
  borderRadius: '10px',
  overflow: 'hidden',
  alignSelf: 'flex-start',
  backgroundColor: COLORS.white,
});

export const MonthDowRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  background: '#f5f5f5',
  borderBottom: '1px solid #e0e0e0',
});

export const MonthDowCell = styled(Box, {
  shouldForwardProp: prop => prop !== 'dayColor',
})(({ dayColor }) => ({
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 600,
  color: dayColor,
  borderRight: '1px solid #ececec',
}));

export const MonthWeekRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  borderBottom: '1px solid #ececec',
});

export const MonthDayCell = styled(Box, {
  shouldForwardProp: prop => !['isToday', 'isSelected', 'cellHeight'].includes(prop),
})(({ isToday, isSelected, cellHeight = 128 }) => ({
  height: `${cellHeight}px`,
  borderRight: '1px solid #ececec',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: isToday ? 'rgba(15, 127, 133, 0.04)' : COLORS.white,
  boxShadow: isSelected ? 'inset 0 0 0 2px #4AB3BC' : 'none',
}));

export const MonthCellHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '6px 8px 3px',
  flexShrink: 0,
});

export const MonthDayNumber = styled('span', {
  shouldForwardProp: prop => prop !== 'numColor',
})(({ numColor }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: numColor,
  lineHeight: '22px',
}));

export const MonthTodayBadge = styled('span')({
  width: '22px',
  height: '22px',
  borderRadius: '50%',
  backgroundColor: '#0F7F85',
  color: COLORS.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 700,
});

export const MonthCellCount = styled('span')({
  fontSize: '10px',
  color: '#a6a6a6',
});

export const MonthEventList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  padding: '0 5px',
});

export const MonthEventRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  height: '19px',
  flexShrink: 0,
  overflow: 'hidden',
  borderRadius: '4px',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
  // 충돌 포커스(붙여넣기·드래그 실패 시 부딪힌 일정 강조)
  transition: 'box-shadow 200ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&.focus-flash': {
    boxShadow: '0 0 0 2px rgba(15, 127, 133, 0.7)',
    backgroundColor: 'rgba(15, 127, 133, 0.08)',
  },
});

export const MonthEventBar = styled('span', {
  shouldForwardProp: prop => prop !== 'barColor',
})(({ barColor }) => ({
  width: '3px',
  height: '12px',
  borderRadius: '2px',
  backgroundColor: barColor,
  flexShrink: 0,
}));

// 각 글자 라인박스를 1로 조여 행의 alignItems:center로 세로 중앙 정렬
export const MonthEventTime = styled('span')({
  fontSize: '10px',
  lineHeight: 1,
  color: '#8c8c8c',
  fontVariantNumeric: 'tabular-nums',
  flexShrink: 0,
});

// 강사명을 돋보이게 하려고 시간과 동일한 연한 톤·크기로 맞춘다
export const MonthEventName = styled('span')({
  fontSize: '10px',
  lineHeight: 1,
  fontWeight: 500,
  color: '#8c8c8c',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const MonthMoreLabel = styled('span')({
  fontSize: '10px',
  color: '#0F7F85',
  fontWeight: 600,
  paddingLeft: '7px',
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
});

// '+n건 더' 확장 레이어 — 칸 위에 겹쳐 그날 일정을 전부 보여준다 (위치·크기는 호출부가 계산)
export const MonthMorePopover = styled(Box)({
  position: 'fixed',
  zIndex: 1200,
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  backgroundColor: COLORS.white,
  border: '1px solid #4AB3BC',
  borderRadius: '6px',
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.18)',
  cursor: 'pointer',
});

export const MonthMorePopoverBody = styled(MonthEventList)({
  overflowY: 'auto',
  paddingBottom: '6px',
  minHeight: 0,
});

// 월별 패널 — 강사(임직원)별 그룹
export const PanelTrainerGroup = styled(Box)({
  padding: '14px 20px 10px',
  borderBottom: '1px solid #f5f5f5',
});

export const PanelTrainerGroupHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '6px',
});

export const PanelTrainerDot = styled('span', {
  shouldForwardProp: prop => prop !== 'dotColor',
})(({ dotColor }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: dotColor,
}));

export const PanelTrainerName = styled('span')({
  fontSize: '14px',
  fontWeight: 700,
  color: '#333',
});

export const PanelTrainerCount = styled('span')({
  fontSize: '12px',
  color: '#a6a6a6',
});

// 행은 세로 스택이다. 첫 줄은 PanelItemTopLine 이 가로로 그리고, 상담 행만 아래에 요약 줄이 붙는다.
export const PanelGroupItemRow = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  padding: '7px 0 7px 16px',
  borderRadius: '6px',
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#fafafa' },
  '&:hover .panel-jump-pill': {
    opacity: 1,
    transform: 'translateY(-50%) translateX(0)',
    pointerEvents: 'auto',
  },
});

export const PanelItemTopLine = styled(Box)({
  display: 'flex',
  // 시간(14px)·이름(13px)은 크기가 달라도 center 정렬에서 베이스라인 오차가 0.5px 로 눈에 띄지 않는다.
  // baseline 정렬은 오차를 0.22px 로 줄이는 대신 텍스트 쌍이 행 중심에서 2.85px 떠올라 바·배지와 어긋난다.
  alignItems: 'center',
  gap: '12px',
  minWidth: 0,
});

// 상담 내용 줄 — 첫 줄의 이름 위치(바 3px + gap 12px + 시간 46px + gap 12px)에 맞춰 들여쓴다.
// 2줄까지만 보여주고 원문은 카드 클릭 시 상담 상세 모달에서 본다.
export const PanelConsultContent = styled('div')({
  marginLeft: '73px',
  marginTop: '3px',
  paddingRight: '12px',
  fontSize: '12px',
  lineHeight: 1.5,
  color: '#8A9096',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  wordBreak: 'break-word',
});

// 상세 패널 그룹레슨 행 호버 슬라이드 액션 (시안 4a) — 그룹레슨 화면의 해당 수업으로 이동
export const PanelRowJumpPill = styled('button')({
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%) translateX(14px)',
  opacity: 0,
  pointerEvents: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  background: '#0F7F85',
  color: '#fff',
  border: 'none',
  borderRadius: '999px',
  padding: '6px 13px',
  fontSize: '12px',
  fontWeight: 700,
  fontFamily: 'inherit',
  boxShadow: '0 2px 8px rgba(15,127,133,0.35)',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'transform 0.18s ease, opacity 0.18s ease',
});

// 시간은 한 칸 키워(14px) 행의 기준점 역할을 하고, 이름은 굵기·명도로 강조한다.
// lineHeight 1 은 상자가 글자보다 낮아 '(0/1)' 같은 디센더가 overflow:hidden 에 잘리므로 1.45 를 유지한다.
// 정렬은 행의 alignItems:center 가 담당 (PanelGroupItemRow 주석 참고).
export const PanelItemTime = styled('span')({
  fontSize: '14px',
  lineHeight: 1.45,
  color: '#666',
  fontVariantNumeric: 'tabular-nums',
  width: '46px',
  flexShrink: 0,
});

export const PanelItemName = styled('span')({
  fontSize: '13px',
  lineHeight: 1.45,
  fontWeight: 600,
  color: '#333',
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// 그룹레슨 인원 표기 — 숫자를 라벨 베이스라인에 그대로 두면 한글보다 낮아 보여 0.3px 띄운다.
export const PanelItemCount = styled('span')({
  marginLeft: '4px',
  verticalAlign: '0.3px',
  fontVariantNumeric: 'tabular-nums',
});

// ============================================
// 주간 보기 (Week View)
// ============================================
export const WeekGridContainer = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  border: '1px solid #E5E7EB',
  borderRadius: '10px',
  overflow: 'hidden',
  backgroundColor: COLORS.white,
});

// 주간 머리글 높이 — 그룹 스케줄 표 머리글(위아래 8px + 한 줄 + 아래 2px 선)과 같다. 시간 열 머리 칸도 같은 높이
const WEEK_HEADER_HEIGHT = '40px';

export const WeekTimeColumn = styled(Box)({
  width: '56px',
  flexShrink: 0,
  background: '#FAFBFC',
  borderRight: '1px solid #E5E7EB',
});

export const WeekTimeHeaderSpacer = styled(Box)({
  height: WEEK_HEADER_HEIGHT,
  borderBottom: '2px solid #e0e0e0',
  background: '#fafafa',
  boxSizing: 'border-box',
});

export const WeekTimeLabel = styled(Box, {
  shouldForwardProp: prop => prop !== 'labelHeight',
})(({ labelHeight = 52 }) => ({
  height: `${labelHeight}px`,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '11px',
  fontWeight: 500,
  color: '#9CA3AF',
  borderBottom: '1px solid #F3F4F6',
}));

export const WeekDayColumn = styled(Box, {
  shouldForwardProp: prop => prop !== 'isToday' && prop !== 'isSelected',
})(({ isToday, isSelected }) => ({
  flex: 1,
  minWidth: 0,
  borderRight: '1px solid #f0f0f0',
  '&:last-child': { borderRight: 'none' },
  cursor: 'pointer',
  backgroundColor: isToday ? 'rgba(15, 127, 133, 0.03)' : 'transparent',
  boxShadow: isSelected ? 'inset 0 0 0 2px #4AB3BC' : 'none',
}));

// 그룹 스케줄 표 머리글과 같은 톤 — 'D (요일)' 한 줄 + 오늘 배지(GroupLessonStyled.DayLabel·TodayBadge)
export const WeekDayHeader = styled(Box, {
  shouldForwardProp: prop => prop !== 'isToday',
})(({ isToday }) => ({
  height: WEEK_HEADER_HEIGHT,
  borderBottom: '2px solid #e0e0e0',
  background: isToday ? '#e8f5f5' : '#fafafa',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// 가로선 = 표 칸 경계(unitMinutes 마다). 1시간 칸은 기존처럼 30분 보조선(연한 선)을 함께 그린다.
export const WeekDayBody = styled(Box, {
  shouldForwardProp: prop => prop !== 'bodyHeight' && prop !== 'hourHeight' && prop !== 'unitMinutes',
})(({ bodyHeight, hourHeight = 52, unitMinutes = 60 }) => ({
  position: 'relative',
  height: `${bodyHeight}px`,
  ...(unitMinutes === 60
    ? {
        backgroundImage:
          'linear-gradient(to bottom, #f0f0f0 1px, transparent 1px), linear-gradient(to bottom, #F3F4F6 1px, transparent 1px)',
        backgroundSize: `100% ${hourHeight}px, 100% ${hourHeight / 2}px`,
      }
    : {
        backgroundImage: 'linear-gradient(to bottom, #f0f0f0 1px, transparent 1px)',
        backgroundSize: `100% ${(hourHeight * unitMinutes) / 60}px`,
        // 범위가 칸으로 안 나눠지면(2시간 칸) 마지막 짧은 칸의 아래 경계가 반복 선에 안 걸린다 — 바닥 선을 따로 긋는다
        boxShadow: 'inset 0 -1px 0 #f0f0f0',
      }),
  backgroundPosition: '0 -1px',
}));

export const WeekEventCard = styled(Box, {
  shouldForwardProp: prop =>
    !['cardTop', 'cardHeight', 'cardLeft', 'cardRight', 'cardWidth', 'bgColor', 'borderColor', 'cardZIndex'].includes(
      prop,
    ),
})(({ cardTop, cardHeight, cardLeft, cardRight, cardWidth, bgColor, borderColor, cardZIndex = 2 }) => ({
  position: 'absolute',
  top: `${cardTop}px`,
  height: `${cardHeight}px`,
  left: cardLeft,
  right: cardRight,
  width: cardWidth,
  backgroundColor: bgColor,
  border: `1px solid ${borderColor}`,
  borderRadius: '6px',
  padding: '3px 8px',
  display: 'flex',
  justifyContent: 'space-between',
  gap: '4px',
  overflow: 'hidden',
  zIndex: cardZIndex,
  // 충돌 포커스(붙여넣기·드래그 실패 시 부딪힌 카드 강조)
  transition: 'box-shadow 200ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&.focus-flash': {
    boxShadow: '0 0 0 2px #fff, 0 0 0 4px rgba(15, 127, 133, 0.7)',
    zIndex: 100,
  },
  boxSizing: 'border-box',
  cursor: 'pointer',
  // 동시간 좌우 분할 카드가 잘려 보일 때 hover로 열 전체 폭 확장.
  // :hover 가 아니라 JS 클래스로 켠다 — 펼친 폭 전체가 아니라 원래 자리를 벗어나면 접히게 하려고(주간 보기)
  '&.is-expanded': {
    left: '2px',
    right: '2px',
    width: 'auto',
    zIndex: 30,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
  },
}));

export const WeekEventContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  // 위쪽 정렬 — 뒤 일정이 카드를 덮어도 첫 줄은 남는다 (일간 카드와 같은 규칙)
  justifyContent: 'flex-start',
  gap: '1px',
  overflow: 'hidden',
  minWidth: 0,
  flex: 1,
});

// fontColor 미지정 시 기존 흰색 유지 (상담 카드 등 진한 배경용)
export const WeekEventTime = styled('div', {
  shouldForwardProp: prop => prop !== 'fontColor',
})(({ fontColor }) => ({
  fontSize: '11px',
  color: fontColor || 'rgba(255, 255, 255, 0.9)',
  opacity: fontColor ? 0.85 : 1,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  flexShrink: 0,
}));

// 주간 카드 강사명 — 강사 컬럼이 없는 보기라 가장 강조되는 요소
export const WeekEventTrainer = styled('span', {
  shouldForwardProp: prop => prop !== 'fontColor',
})(({ fontColor }) => ({
  fontWeight: 800,
  fontSize: '11.5px',
  color: fontColor || '#FFFFFF',
  marginRight: '4px',
}));

export const WeekEventLabel = styled('div', {
  shouldForwardProp: prop => prop !== 'fontColor',
})(({ fontColor }) => ({
  fontSize: '12px',
  fontWeight: 700,
  color: fontColor || COLORS.white,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flexShrink: 0,
}));

export const WeekEventSession = styled(Box, {
  shouldForwardProp: prop => prop !== 'fontColor',
})(({ fontColor }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  paddingBottom: '2px',
  flexShrink: 0,
  fontSize: '11px',
  color: fontColor || 'rgba(255, 255, 255, 0.9)',
  opacity: fontColor ? 0.85 : 1,
}));

export const WeekCurrentTimeLine = styled(Box, {
  shouldForwardProp: prop => prop !== 'topPosition',
})(({ topPosition }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: `${topPosition}px`,
  height: '2px',
  backgroundColor: COLORS.red,
  zIndex: 10,
  pointerEvents: 'none',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-1px',
    top: '-4px',
    width: '10px',
    height: '10px',
    backgroundColor: COLORS.red,
    borderRadius: '50%',
  },
}));

// 주간 패널 아이템
export const WeekPanelItemRow = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '9px 0',
  borderBottom: '1px solid #f7f7f7',
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#fafafa' },
  '&:hover .panel-jump-pill': {
    opacity: 1,
    transform: 'translateY(-50%) translateX(0)',
    pointerEvents: 'auto',
  },
});

export const WeekPanelItemBar = styled('span', {
  shouldForwardProp: prop => prop !== 'barColor',
})(({ barColor }) => ({
  width: '3px',
  height: '26px',
  borderRadius: '2px',
  backgroundColor: barColor,
  flexShrink: 0,
}));

export const WeekPanelItemTime = styled('span')({
  fontSize: '13px',
  color: '#666',
  fontVariantNumeric: 'tabular-nums',
  width: '88px',
  flexShrink: 0,
});

export const WeekPanelItemText = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  flex: 1,
  minWidth: 0,
});

export const WeekPanelItemName = styled('span')({
  fontSize: '14px',
  fontWeight: 600,
  color: '#333',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const WeekPanelItemSub = styled('span')({
  fontSize: '12px',
  color: '#a6a6a6',
});

// 주간 그리드 빈칸 호버 미리보기 — 그룹레슨 EventCell 호버 박스와 동일한 시각
export const WeekHoverSlot = styled('div', {
  shouldForwardProp: prop => prop !== 'slotTop' && prop !== 'slotHeight',
})(({ slotTop, slotHeight }) => ({
  position: 'absolute',
  top: `${slotTop + 2}px`,
  left: '2px',
  right: '2px',
  height: `${slotHeight - 4}px`,
  backgroundColor: 'rgba(74, 179, 188, 0.15)',
  border: '2px dashed #4AB3BC',
  borderRadius: '6px',
  pointerEvents: 'none',
  zIndex: 1,
}));

// 주간 패널 무한 스크롤 안내 문구
export const WeekPanelLoadMoreHint = styled(Box)({
  textAlign: 'center',
  padding: '10px 0',
  fontSize: '12px',
  color: '#8c8c8c',
});

// ============================================
// Trainer Filter Modal
// ============================================
export const TrainerFilterModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '400px',
  maxHeight: '500px',
  backgroundColor: COLORS.white,
  borderRadius: '10px',
  overflow: 'hidden',
});

export const TrainerFilterHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  borderBottom: '1px solid #e0e0e0',
  backgroundColor: '#333',
});

export const TrainerFilterHeaderTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: COLORS.white,
});

export const TrainerFilterSelectAll = styled(Box)({
  padding: '12px 20px',
  borderBottom: '1px solid #e0e0e0',
});

export const TrainerFilterList = styled(Box)({
  maxHeight: '300px',
  overflowY: 'auto',
  padding: '8px 20px',
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
});

export const TrainerFilterItem = styled(Box)({
  display: 'flex',
  width: '100%',
  margin: 0,
  padding: '8px 0',
  borderBottom: '1px solid #f0f0f0',
  '&:last-child': { borderBottom: 'none' },
});

export const TrainerFilterButtonGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  padding: '16px 20px',
  borderTop: '1px solid #e0e0e0',
});

export const TrainerFilterResetButton = styled(Button)({
  flex: 1,
  borderColor: '#d9d9d9',
  color: '#666',
  '&:hover': { borderColor: '#999', backgroundColor: '#f5f5f5' },
});

export const TrainerFilterApplyButton = styled(Button)({
  flex: 1,
  backgroundColor: COLORS.primary,
  color: COLORS.white,
  '&:hover': { backgroundColor: COLORS.primaryHover },
});

// ============================================
// Event Detail Menu
// ============================================
export const EventDetailMenuContainer = styled(Box)({
  width: '230px',
  padding: 0,
  marginTop: '-10px',
  marginBottom: '-10px',
});

export const EventDetailMenuHeader = styled(Box)({
  backgroundColor: COLORS.primary,
  padding: '8px 12px',
  borderRadius: '10px 10px 0 0',
  marginLeft: '-12px',
  marginRight: '-12px',
  marginTop: '-10.4px',
});

export const EventDetailMenuTitle = styled(Typography)({
  color: COLORS.white,
  fontWeight: 600,
  fontSize: '13px',
});

export const EventDetailMenuSubtitle = styled(Typography)({
  color: COLORS.white,
  fontSize: '12px',
});

export const EventDetailMenuBody = styled(Box)({
  padding: '12px 0',
});

export const EventDetailMenuRow = styled(Typography)({
  fontSize: '12px',
  color: '#333',
  marginBottom: '4px',
});

export const EventDetailMenuButtonGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginTop: '12px',
});

export const EventDetailMenuButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'buttonVariant',
})(({ buttonVariant }) => ({
  flex: 1,
  borderRadius: '8px',
  fontSize: '11px',
  padding: '4px 0',
  ...(buttonVariant === 'primary' && {
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    '&:hover': { backgroundColor: COLORS.primaryHover },
  }),
  ...(buttonVariant === 'secondary' && {
    borderColor: COLORS.primary,
    color: COLORS.primary,
    border: `1px solid ${COLORS.primary}`,
    '&:hover': { borderColor: COLORS.primaryHover, backgroundColor: COLORS.primaryBg },
  }),
}));

// ============================================
// Cancel Confirm Modal
// ============================================
export const CancelConfirmModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '400px',
  height: '200px',
  backgroundColor: COLORS.white,
  borderRadius: '10px',
  border: 'none',
});

export const CancelConfirmContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
});

export const CancelConfirmTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
});

export const CancelConfirmText = styled(Typography)({
  textAlign: 'center',
});

export const CancelConfirmButtonGroup = styled(Box)({
  display: 'flex',
  gap: '20px',
});

export const CancelConfirmBackButton = styled(Button)({
  backgroundColor: '#c5c5c5',
  color: COLORS.white,
  borderRadius: '10px',
  '&:hover': { backgroundColor: '#c5c5c5' },
});

export const CancelConfirmSubmitButton = styled(Button)({
  borderRadius: '10px',
  backgroundColor: COLORS.primary,
  color: COLORS.white,
  '&:hover': { backgroundColor: COLORS.primaryHover },
});

// ============================================
// Tooltip Styles (for MUI Tooltip)
// ============================================
export const TooltipContainer = styled(Box)({
  padding: 0,
});

export const TooltipHeader = styled(Box)({
  backgroundColor: COLORS.primary,
  padding: '8px 12px',
  borderRadius: '10px 10px 0 0',
  marginLeft: '-10.4px',
  marginRight: '-10.4px',
  marginTop: '-10.4px',
});

export const TooltipTitle = styled(Typography)({
  color: COLORS.white,
  fontWeight: 600,
  fontSize: '13px',
});

export const TooltipSubtitle = styled(Typography)({
  color: COLORS.white,
  fontSize: '12px',
});

export const TooltipBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  margin: '0 -10px -10px',
});

export const TooltipRow = styled(Typography)({
  fontSize: '12px',
  color: '#333',
  padding: '3px 12px',
});

// Consultation Tooltip (Blue theme)
export const ConsultationTooltipHeader = styled(TooltipHeader)({
  backgroundColor: '#4AB3BC',
});

// ============================================
// Generic Modal Components
// ============================================
export const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: COLORS.white,
  borderRadius: '10px',
  overflow: 'hidden',
});

export const ModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  borderBottom: '1px solid #e0e0e0',
});

export const ModalHeaderTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
});

export const ModalBody = styled(Box)({
  padding: '16px 20px',
});

export const ModalFooter = styled(Box)({
  display: 'flex',
  gap: '8px',
  padding: '16px 20px',
  borderTop: '1px solid #e0e0e0',
});

// ============================================
// Trainer List Components (for filter modal)
// ============================================
export const TrainerListContainer = styled(Box)({
  maxHeight: '300px',
  overflowY: 'auto',
  padding: '8px 20px',
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
});

export const TrainerListItem = styled(Box)({
  display: 'flex',
  width: '100%',
  margin: 0,
  padding: '8px 0',
  borderBottom: '1px solid #f0f0f0',
  '&:last-child': { borderBottom: 'none' },
});

// ============================================
// Event Menu Components (for popup menu)
// ============================================
export const EventMenuContainer = styled(Box)({
  width: '230px',
  padding: 0,
  marginTop: '-10px',
  marginBottom: '-10px',
});

export const EventMenuHeader = styled(Box)({
  backgroundColor: COLORS.primary,
  padding: '8px 12px',
  borderRadius: '10px 10px 0 0',
});

export const EventMenuBody = styled(Box)({
  padding: '12px',
});

export const EventMenuButtonGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginTop: '12px',
});

// ============================================
// 스케줄 리뉴얼 — 상태 뱃지·카운트·요약 칩·상태 바·툴팁
// ============================================

// 일간 카드 우측 상단 출석/결석 pill 뱃지
export const DayCardStatusBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'badgeBg' && prop !== 'badgeFont',
})(({ badgeBg, badgeFont }) => ({
  backgroundColor: badgeBg,
  color: badgeFont,
  borderRadius: '999px',
  padding: '1px 7px',
  fontSize: '10px',
  fontWeight: 800,
  lineHeight: 1.6,
  flexShrink: 0,
}));

// 일간 강사 컬럼 헤더의 예약/출석/결석 카운트 줄
export const TrainerHeaderCounts = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '8px',
  marginTop: '2px',
});

export const TrainerHeaderCountItem = styled('span', {
  shouldForwardProp: prop => prop !== 'countColor',
})(({ countColor }) => ({
  fontSize: '11px',
  fontWeight: 600,
  color: countColor,
  whiteSpace: 'nowrap',
}));

// 헤더 우측 요약 칩 (예약 | 출석 | 결석 — 월/주/일 공통)
export const HeaderSummaryChips = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  padding: '1px 5px',
  border: '1px solid #E4E9EA',
  borderRadius: '8px',
  backgroundColor: COLORS.white,
});

// 요약 카운트 겸 상태 필터 토글 — 켜진 상태는 상태색 테두리로만 표시한다 (배경 없음).
// 라벨 색은 항상 #5A6468 로 두고(대비 6.5:1) 상태색은 카운트 숫자와 테두리에만 쓴다.
// 비활성 테두리를 transparent 로 둬야 토글 시 레이아웃이 흔들리지 않는다.
export const SummaryChipItem = styled('button', {
  shouldForwardProp: prop => prop !== 'countColor' && prop !== 'isActive',
})(({ countColor, isActive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 9px',
  border: `1px solid ${isActive ? countColor : 'transparent'}`,
  borderRadius: '6px',
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: 500,
  color: '#5A6468',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
  '&:hover': { backgroundColor: '#F2F5F6' },
  '& b': {
    color: countColor,
    fontWeight: 800,
    marginLeft: '4px',
  },
}));

export const SummaryChipDivider = styled('span')({
  color: '#D9DEE0',
  fontSize: '12px',
});

// 필터가 아닌 표시 전용 요약 칩 (수업 건수·상담 건수·대기 인원) — 토글 칩과 높이·간격을 맞춘다
export const SummaryStaticChip = styled('span', {
  shouldForwardProp: prop => prop !== 'countColor',
})(({ countColor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 9px',
  border: '1px solid transparent',
  fontSize: '13px',
  fontWeight: 500,
  color: '#5A6468',
  whiteSpace: 'nowrap',
  '& b': {
    color: countColor,
    fontWeight: 800,
    marginLeft: '4px',
  },
}));

// 주간 카드/패널 항목 좌측 상태 바
export const WeekEventStatusBar = styled('span', {
  shouldForwardProp: prop => prop !== 'barColor',
})(({ barColor }) => ({
  position: 'absolute',
  left: '2px',
  top: '3px',
  bottom: '3px',
  width: '4px',
  borderRadius: '999px',
  backgroundColor: barColor,
  flexShrink: 0,
}));

// 공용 hover 툴팁 — 상태 뱃지 / 수강권 임박 배너 / 정보 행
export const TooltipStatusBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'badgeBg' && prop !== 'badgeFont',
})(({ badgeBg, badgeFont }) => ({
  backgroundColor: badgeBg,
  color: badgeFont,
  borderRadius: '999px',
  padding: '2px 9px',
  fontSize: '11px',
  fontWeight: 800,
  flexShrink: 0,
}));

export const TooltipTicketBanner = styled(Box, {
  shouldForwardProp: prop => prop !== 'isExpiring',
})(({ isExpiring }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  backgroundColor: isExpiring ? '#FDF3F2' : '#F5F7F8',
  padding: '6px 12px',
  margin: '0 -10px',
  borderBottom: '1px solid #EDF0F2',
  fontSize: '12px',
  color: '#5A6468',
}));

// 수강권 상태 뱃지 — 잔여 세션이 지점 기준 이하면 임박, 아니면 유효
export const TICKET_STATUS_META = {
  valid: { label: '유효', badgeBg: '#2FA36B', badgeFont: '#FFFFFF' },
  expiring: { label: '임박', badgeBg: '#C7362A', badgeFont: '#FFFFFF' },
};

export const TooltipTicketBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'badgeBg' && prop !== 'badgeFont',
})(({ badgeBg, badgeFont }) => ({
  backgroundColor: badgeBg,
  color: badgeFont,
  borderRadius: '999px',
  padding: '1px 8px',
  fontSize: '10.5px',
  fontWeight: 800,
  flexShrink: 0,
}));

// 개인/듀엣 툴팁 헤더
export const TooltipLessonHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  padding: '8px 12px',
  margin: '-10px -10px 0',
  background: '#F5F7F8',
  borderBottom: '1px solid #E0E3E6',
  borderRadius: '8px 8px 0 0',
});

export const TooltipLessonTitle = styled(Typography)({
  color: '#1F2729',
  fontWeight: 700,
  fontSize: '13px',
});

export const TooltipInfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'stretch',
  borderBottom: '1px solid #EDF0F2',
  '&:last-child': {
    borderBottom: 'none',
    '& > span:first-of-type': { borderRadius: '0 0 0 8px' },
  },
});

export const TooltipInfoLabel = styled('span')({
  // 가장 긴 라벨(전화번호)이 들어가는 고정폭 — 최소폭이면 그 행만 넓어져 값 컬럼이 밀린다
  minWidth: '68px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  fontSize: '11.5px',
  fontWeight: 600,
  color: '#7B8A8F',
  padding: '6px 10px',
  background: '#F5F7F8',
  borderRight: '1px solid #EDF0F2',
  display: 'flex',
  alignItems: 'center',
});

export const TooltipInfoValue = styled('span', {
  shouldForwardProp: prop => prop !== 'isAlert',
})(({ isAlert }) => ({
  fontSize: '12px',
  fontWeight: 700,
  color: isAlert ? '#C7362A' : '#2A3235',
  padding: '6px 12px',
  display: 'flex',
  alignItems: 'center',
}));

// 상담 툴팁의 내용 값 — 여러 줄을 허용하되 3줄까지만. 원문은 상담 상세 모달에서 본다.
export const TooltipConsultContent = styled('span')({
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: 1.5,
  color: '#2A3235',
  padding: '6px 12px',
  minWidth: 0,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  overflow: 'hidden',
  wordBreak: 'break-word',
});

// 월간 그리드 행의 강사명 프리픽스
// 월간 행의 주인공 — 크기는 시간·회원명과 같은 10px로 두고 색·굵기로만 강조 (크기 통일로 정렬 흔들림 제거)
export const MonthEventTrainer = styled('span')({
  color: '#2A3235',
  fontSize: '10px',
  lineHeight: 1,
  fontWeight: 700,
  flexShrink: 0,
});
